import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts'
import config from 'constants/config'

export interface OidcConfig {
  authority: string
  clientId: string
  scope: string
}

const DEFAULT_SCOPE = 'openid email offline_access'

export const getOidcConfig = (): OidcConfig => ({
  authority: config.OIDC_AUTHORITY || '',
  clientId: config.OIDC_CLIENT_ID || '',
  scope: DEFAULT_SCOPE,
})

// svc-auth allowlists this exact URL (exact match, subpaths rejected).
export const oidcRedirectUri = (): string => `${window.location.origin}/oidc-callback`

export const createOidcUserStore = (): WebStorageStateStore =>
  new WebStorageStateStore({ store: window.localStorage })

export const createOidcUserManager = (): UserManager => {
  const oidc = getOidcConfig()

  return new UserManager({
    authority: oidc.authority,
    client_id: oidc.clientId,
    scope: oidc.scope,
    redirect_uri: oidcRedirectUri(),
    post_logout_redirect_uri: window.location.origin,
    userStore: createOidcUserStore(),
    // Defaults to true, and must stay off: the library's timer is per-tab, and
    // concurrent renews get the session revoked. useOidcSessionCoordinator
    // drives renew instead, serialised via renewLock.
    automaticSilentRenew: false,
    // Not revokeTokensOnSignout: svc-auth has no revocation_endpoint, so it would
    // throw inside signoutRedirect and abort the end-session redirect.
  })
}

// The key UserManager persists the session under (UserManager._userStoreKey).
const userStoreKey = (userManager: UserManager): string =>
  `user:${userManager.settings.authority}:${userManager.settings.client_id}`

// Not userManager.getUser(): that re-arms the expiry timers, and its Timer floors
// at one second, so calling it from a renew handler is a 1Hz loop.
export const readStoredOidcUser = async(userManager: UserManager): Promise<User | undefined> => {
  const raw = await userManager.settings.userStore.get(userStoreKey(userManager))

  return raw ? User.fromStorageString(raw) : undefined
}
